import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Package, BadgeCheck, ArrowRight, ShoppingCart, Sparkles } from "lucide-react";
import { marketplaceApi, type ProductItem } from "@/lib/api";

/** "AGRICULTURE & FOOD" -> "Agriculture & food" (sentence case). */
function sentenceCase(s: string): string {
  const t = s.trim().toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Fisher–Yates shuffle (returns a new array). */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface CategoryGroup<T> { key: string; name: string; items: T[]; }

/** Group by category, biggest categories first; take the top `maxGroups`
 *  categories and at most `perGroup` listings from each. Uncategorised is
 *  dropped — the home page teaser only shows named categories. */
export function topCategoryGroups<T extends { category: number | null; category_name: string | null }>(
  items: T[], maxGroups: number, perGroup: number,
): CategoryGroup<T>[] {
  const map = new Map<string, CategoryGroup<T>>();
  items.forEach(item => {
    if (item.category == null || !item.category_name) return;
    const key = String(item.category);
    const entry = map.get(key) || { key, name: item.category_name, items: [] as T[] };
    entry.items.push(item);
    map.set(key, entry);
  });
  return Array.from(map.values())
    .sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name))
    .slice(0, maxGroups)
    .map(g => ({ ...g, items: g.items.slice(0, perGroup) }));
}

const ProductCard = ({ product, index }: { product: ProductItem; index: number }) => {
  const discount = product.discount_price
    ? Math.round(((+product.price - +product.discount_price) / +product.price) * 100)
    : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 32, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.05, 0.5), ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.2 } }}
      className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-gold/40 transition-[box-shadow,border-color]"
    >
      <Link to={`/marketplace/product/${product.slug}`} className="block">
        <div className="aspect-square bg-gray-100 relative overflow-hidden">
          {product.main_image_url ? (
            <img
              src={product.main_image_url}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-300">
              <Package size={40} />
            </div>
          )}

          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {product.is_sponsored && (
              <span className="inline-flex items-center gap-1 bg-gold text-gold-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded shadow-sm">
                <Sparkles size={10} /> Sponsored
              </span>
            )}
            {discount > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold uppercase px-2 py-1 rounded">
                -{discount}%
              </span>
            )}
          </div>

          <div className="absolute inset-x-2 bottom-2 translate-y-12 group-hover:translate-y-0 transition-transform">
            <div className="bg-black/90 backdrop-blur text-white text-xs font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5">
              <ShoppingCart size={14} /> Quick view
            </div>
          </div>
        </div>

        <div className="p-3">
          {product.category_name && (
            <p className="text-[10px] text-black tracking-wide mb-1 truncate">{sentenceCase(product.category_name)}</p>
          )}
          <h3 className="text-[13px] font-semibold text-black line-clamp-2 group-hover:text-gold transition-colors leading-snug min-h-[2.6em]">
            {product.name}
          </h3>

          <div className="mt-2.5">
            {product.discount_price ? (
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-sm font-bold text-black">{product.currency || "$"}{product.discount_price}</span>
                <span className="text-[11px] text-gray-400 line-through">{product.currency || "$"}{product.price}</span>
              </div>
            ) : Number(product.price) > 0 ? (
              <span className="text-sm font-bold text-black">{product.currency || "$"}{product.price}</span>
            ) : (
              <span className="text-[11px] font-semibold text-gold">Request quote</span>
            )}
          </div>

          {product.owner_is_verified && (
            <div className="flex items-center gap-1 mt-2.5 pt-2.5 border-t border-gray-100 text-[10px] font-medium text-gold">
              <BadgeCheck size={12} /> Verified member
            </div>
          )}
        </div>
      </Link>
      {/* Animated bottom accent line on hover */}
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
    </motion.div>
  );
};

const FeaturedProducts = () => {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const all = await marketplaceApi.listProducts();
        if (alive) setProducts(shuffle(all));
      } catch {
        /* ignore — section just stays empty */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Four best-stocked categories, one 5-wide row each.
  const groups = useMemo(() => topCategoryGroups(products, 4, 5), [products]);

  if (!loading && groups.length === 0) return null;

  return (
    <section className="py-12 bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-6 gap-4">
          <h2 className="text-2xl md:text-3xl font-bold text-black leading-snug">Products</h2>
          <Link
            to="/marketplace/products"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline shrink-0"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-100" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-9">
            {groups.map(group => (
              <div key={group.key}>
                <div className="flex items-end justify-between gap-4 mb-4 pb-2 border-b border-gray-200">
                  <h3 className="text-lg md:text-xl font-bold text-black truncate">
                    {sentenceCase(group.name)}
                  </h3>
                  <Link
                    to={`/marketplace/products?category=${group.key}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline shrink-0"
                  >
                    See all <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5">
                  {group.items.map((product, i) => (
                    <ProductCard key={product.id} product={product} index={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/marketplace/products"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline"
          >
            View all products <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
